import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, LayoutDashboard, LogOut, Mail, Settings, Trophy, Users, Zap } from "lucide-react";
import { api, clearStudentId } from "@/lib/api";
import SettingsModal from "@/components/SettingsModal";

export default function Navbar({ student }) {
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    window.location.href = "/";
    clearStudentId();
  };
  return (
    <nav className="h-16 lg:h-20 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-40" data-testid="top-navbar">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-full flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 shrink-0" data-testid="nav-logo-home">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_student-genius-26/artifacts/kbshcq5q_Branding-02.png"
            alt="SmartGuru Logo Icon"
            className="h-9 w-9 object-contain shrink-0"
            data-testid="nav-logo-icon"
          />
          <span className="font-heading font-extrabold text-lg hidden sm:inline bg-gradient-to-r from-[#9A3DB2] via-[#4F4CB9] to-[#E73F9A] bg-clip-text text-transparent">SmartGuru.io</span>
        </button>
        <div className="flex items-center gap-0.5 sm:gap-2 min-w-0">
          <button onClick={() => navigate("/curriculum")} data-testid="nav-curriculum-link"
            className="px-2 sm:px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1.5">
            <LayoutDashboard size={16} /> <span className="hidden md:inline">Curriculum</span>
          </button>
          <button onClick={() => navigate("/mastery")} data-testid="nav-mastery-link"
            className="px-2 sm:px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1.5">
            <Trophy size={16} /> <span className="hidden md:inline">Mastery</span>
          </button>
          <button onClick={() => navigate("/parent")} data-testid="nav-parent-link"
            className="px-2 sm:px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1.5">
            <Users size={16} /> <span className="hidden md:inline">Parent View</span>
          </button>
          {student && typeof student.trial_days_left === "number" && (
            <div data-testid="nav-trial-badge" className="hidden sm:flex items-center gap-1 bg-accent/15 text-accent px-2 sm:px-2.5 py-1.5 rounded-full border border-accent/30 shrink-0 text-xs font-bold">
              <Zap size={12} /> {student.trial_days_left}d left in trial
            </div>
          )}
          {student && (
            <div data-testid="nav-streak-xp-badge" className="ml-1 sm:ml-2 flex items-center gap-1.5 bg-secondary/15 text-secondary-foreground px-2 sm:px-3 py-1.5 rounded-full border border-secondary/30 shrink-0">
              <Flame size={15} className="text-secondary" />
              <span className="text-xs font-bold">{student.streak_count || 0}d</span>
              <span className="hidden sm:inline text-xs text-muted-foreground">· Lvl {student.level} · {student.xp || 0} XP</span>
            </div>
          )}
          <button onClick={() => setSettingsOpen(true)} data-testid="nav-settings-button" title="Voice settings"
            className="ml-0.5 sm:ml-1 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1.5 shrink-0">
            <Settings size={16} />
          </button>
          <button onClick={() => navigate("/contact")} data-testid="nav-contact-link" title="Contact Us"
            className="px-2 sm:px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1.5 shrink-0">
            <Mail size={16} /> <span className="hidden md:inline">Contact</span>
          </button>
          <button onClick={handleLogout} data-testid="nav-logout-button" title="Log out"
            className="ml-0.5 sm:ml-1 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center gap-1.5 shrink-0">
            <LogOut size={16} /> <span className="hidden md:inline">Log out</span>
          </button>
        </div>
      </div>
      {student && <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} student={student} />}
    </nav>
  );
}
